import type { ContractTemplateStatus, ContractTemplateView } from "@closer/shared";
import { CONTRACT_TEMPLATE_STATUS_NAMES } from "@closer/shared";
import {
  Badge,
  Button,
  EmptyState,
  FilterBar,
  Input,
  PageHeader,
  Select,
  Skeleton,
  Table,
} from "@closer/ui";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Link, useSearchParams } from "react-router";

import { ContractIcon } from "../../components/icons";
import { StateView } from "../../components/StateView";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { formatDate } from "../../lib/format";
import { usePreferences } from "../../lib/preferences";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";

/**
 * ŞABLON LİSTESİ (CLOS-125 `[107]`).
 *
 * ### ⚠ Süzgeçler ADRESTE
 *
 * Arama, tür ve durum `?q=&status=` olarak URL'de duruyor: bir hukukçu "bizim NDA
 * kalıpları" görünümünü meslektaşına bağlantı olarak gönderebilmeli. Bileşen state'inde
 * tutsaydık aynı ekranı iki kişi asla aynı anda göremezdi.
 *
 * ### ⚠ Sayfalama SUNUCUDA
 *
 * Kart: *"liste SUNUCU TARAFLI sayfalanır."* Toplam sayı ve sayfa içeriği aynı sorgudan
 * geliyor; istemcide kesmek, ikinci sayfada var olmayan kayıtlar göstermek olurdu.
 *
 * ### ⚠ Pasif şablonlar varsayılan olarak YOK
 *
 * Kart: *"arşiv şablonlar uygun filtre olmadan karışmaz."* Kararı sunucu veriyor; ekran
 * yalnız süzgeci sunuyor.
 *
 * ### UI Tur 2 §7.13 — ATLANANLAR
 *
 * Mockup "Hazır/Kişisel/Organizasyon/Müvekkil Özel" Tabs'ı, "Dışa Aktar ▾", satır sonu
 * "Bu Şablondan Oluştur" (yalnız Hazır kapsamında) ve sağdaki 280px ipucu rayı ÜRÜNDE
 * karşılığı olmadığı için çizilmiyor: tek düz kütüphane var (kapsam alanı yok), dışa
 * aktarma ucu yok, "hazır şablon" kavramı `isSystem` alanıyla var ama listede ayrı bir
 * "bundan oluştur" kısayolu şart koşulmamış (detay sayfasında zaten var). Spec'in kendi
 * kuralı ("ürün metni yoksa ATLANIR", "tek kapsam varsa Tabs ÇİZİLMEZ") bu kararları
 * doğruluyor.
 */

const LIMIT = 20;

/**
 * §7.13 tablo kolonları — spec'in "Kategori / Aktif Versiyon / Son Güncelleme" istediği
 * kolonlar `ContractTemplateView`'da YOK: alan tek katalog kodu (`contractType`, kategori
 * değil), sürüm sayısı var ama "v1.3" gibi semantik bir sürüm dizesi yok, `updatedAt` hiç
 * gelmiyor (yalnız `createdAt`). Türetilemeyen kolon ÇİZİLMEZ (ClientsPage/HomePage'de
 * kurulan kural) — bu yüzden kolonlar üründe GERÇEKTEN var olan alanlarla sınırlı.
 */
function sablonKolonlari(locale: string): ColumnDef<ContractTemplateView>[] {
  return [
    {
      accessorKey: "name",
      header: "Şablon",
      cell: ({ row }) => {
        const sablon = row.original;
        return (
          <Link to={`/app/sablonlar/${sablon.id}`} className="group flex items-start gap-3">
            <span
              aria-hidden="true"
              className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary"
            >
              <ContractIcon className="size-4.5" />
            </span>
            <span className="min-w-0">
              <span className="block font-medium text-fg-heading group-hover:text-primary">
                {sablon.name}
              </span>
              {sablon.description ? (
                <span className="mt-0.5 block text-xs text-fg-soft">{sablon.description}</span>
              ) : null}
              <span className="mt-1 block text-xs text-fg-soft">
                {sablon.bodyKind === "file" ? "Dosya" : "Metin"}
                {sablon.contractType ? ` · ${sablon.contractType}` : ""}
              </span>
            </span>
          </Link>
        );
      },
    },
    {
      accessorKey: "status",
      header: "Durum",
      cell: ({ row }) => (
        <Badge tone={row.original.status === "active" ? "success" : "neutral"}>
          {CONTRACT_TEMPLATE_STATUS_NAMES[row.original.status]}
        </Badge>
      ),
    },
    { accessorKey: "createdByName", header: "Oluşturan" },
    {
      accessorKey: "createdAt",
      header: "Oluşturulma",
      cell: ({ row }) => formatDate(row.original.createdAt, locale),
    },
  ];
}

export function TemplatesPage() {
  const { activeTenantId, can } = useAuth();
  const { preferences } = usePreferences();
  const tenantId = activeTenantId!;
  const [params, setParams] = useSearchParams();

  const q = params.get("q") ?? "";
  const status = (params.get("status") ?? "") as ContractTemplateStatus | "";
  const page = Number(params.get("page") ?? "1");

  const sorgu = useQuery({
    queryKey: tenantKey(tenantId, "templates", q, status, String(page)),
    queryFn: async () => {
      const adres = new URLSearchParams({ page: String(page), limit: String(LIMIT) });
      if (q) adres.set("q", q);
      if (status) adres.set("status", status);
      return api<ContractTemplateView[]>(`/api/templates?${adres.toString()}`, { tenantId });
    },
  });

  const guncelle = (alan: string, deger: string) => {
    const yeni = new URLSearchParams(params);
    if (deger) yeni.set(alan, deger);
    else yeni.delete(alan);
    /** Süzgeç değişince ilk sayfaya dönülüyor: 5. sayfada kalmak boş ekran demek. */
    if (alan !== "page") yeni.delete("page");
    setParams(yeni, { replace: true });
  };

  const toplam = sorgu.data?.meta?.total ?? 0;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Şablonlar"
        description="Şablonlarınızı oluşturun, yönetin ve sözleşme üretiminde kullanın."
        actions={
          can("template.create") ? (
            <Link to="/app/sablonlar/yeni">
              <Button type="button">Yeni şablon</Button>
            </Link>
          ) : null
        }
      />

      {/*
        ⚠ §7.13 kişisel/organizasyon/müvekkil-özel Tabs istiyor, AMA spec'in kendi kuralı
        "tek kapsam varsa Tabs ÇİZİLMEZ" — üründe (`ContractTemplateListQuery`) tek düz
        kütüphane var, kapsam alanı yok. Tabs bilinçli olarak çizilmiyor.

        Kategori/Etiket süzgeci de aynı sebeple yok: `contractType` metinsel bir katalog
        kodu, ayrı bir "kategori" ya da "etiket" alanı üründe bulunmuyor. FilterBar yalnız
        ZATEN VAR OLAN iki süzgeci (arama + durum) taşıyor; yeni bir süzgeç eklenmedi.
      */}
      <FilterBar
        search={
          <Input
            aria-label="Şablon ara"
            placeholder="Şablon adı ara…"
            defaultValue={q}
            onBlur={(e) => guncelle("q", e.target.value.trim())}
          />
        }
        filters={[
          <Select
            key="status"
            aria-label="Durum"
            value={status}
            onChange={(e) => guncelle("status", e.target.value)}
          >
            <option value="">Kullanımdakiler</option>
            <option value="inactive">Pasif olanlar</option>
          </Select>,
        ]}
      />

      {sorgu.isPending ? (
        <div role="status" aria-label="Şablonlar yükleniyor">
          <Skeleton lines={5} />
        </div>
      ) : sorgu.error || !sorgu.data ? (
        <StateView
          state={resolveErrorState(sorgu.error, { idempotent: true })}
          onRetry={() => void sorgu.refetch()}
        />
      ) : sorgu.data.data.length === 0 ? (
        /**
         * BOŞ LİSTE ÇIKMAZ OLMAMALI (CLOS-638).
         *
         * ⚠ Ekran yalnız "şablon yok" diyordu; kullanıcı hiç şablonu olmayan bir alanda
         * ilk şablonu nereden açacağını göremiyordu. Başlıktaki düğme sayfanın üstünde
         * kalıyor ve boş durumda gözden kaçıyor — kartın istediği, aksiyonun BOŞ DURUMUN
         * İÇİNDE sunulması.
         *
         * ⚠ Aksiyon yalnız ARAMA BOŞKEN sunuluyor: "aramanıza uyan şablon yok" ile
         * "hiç şablon yok" farklı durumlar. Arama sonucu boşken oluşturmaya çağırmak,
         * kullanıcının aradığı şeyi bulamadığını gizlerdi.
         *
         * ⚠ Yetkisiz kullanıcı CTA görmüyor ama mesajı görüyor (kart: "yetkisiz kullanıcı
         * CTA görmez; ancak liste boş/hata durumları anlaşılır kalır").
         */
        <EmptyState
          icon={<ContractIcon />}
          title={q ? "Şablon bulunamadı" : "Henüz şablon yok"}
          description={
            q
              ? "Aramanıza uyan bir şablon bulunamadı."
              : "İlk şablonunuzu oluşturun; sözleşmeleri ondan üretebilirsiniz."
          }
          action={
            !q && can("template.create") ? (
              <Link to="/app/sablonlar/yeni">
                <Button type="button">Yeni şablon oluştur</Button>
              </Link>
            ) : undefined
          }
        />
      ) : (
        <Table
          columns={sablonKolonlari(preferences.locale)}
          data={sorgu.data.data}
          pagination={{
            page,
            limit: LIMIT,
            total: toplam,
            onPageChange: (yeniSayfa) => guncelle("page", String(yeniSayfa)),
          }}
        />
      )}
    </div>
  );
}
